import type { WizardStep } from '../components/PnidWizardSidebar';
import type { PnidWizardState } from '../types';
import {
  getInstrumentEffectiveContext,
  type InstrumentEffectiveContext,
} from '../utils/unifiedInstruments';

export interface RouteTargets {
  routeEditEquipmentId: string | null;
  routeTargetEquipmentId: string | null;
  routeTargetNodeId: string | null;
  routeEditInstrumentId: string | null;
  routeTargetInstrumentId: string | null;
  urlStep: WizardStep | null;
}

export interface ResolveInitialStepInput extends RouteTargets {
  state: PnidWizardState;
}

/** Which of the URL's competing targets the workspace acts on. */
export type WinningRouteTarget = 'equipment' | 'node' | 'instrument' | 'step' | null;

/**
 * The one ranking of the URL's targets. A link can carry several, and only one
 * of them can own the wizard step and the canvas viewport, so both the step
 * resolver and the effects that act on a target ask this.
 *
 * The order is equipment, then line, then instrument, then `?step=`. An element
 * family ranks as one: `edit_equipment_id` and `target_equipment_id` are both
 * "equipment", `edit_instrument_id` and `target_instrument_id` are both
 * "instrument", because the pair differs in whether an editor opens, not in
 * which element the drawing lands on.
 */
export function winningRouteTarget(targets: RouteTargets): WinningRouteTarget {
  if (targets.routeEditEquipmentId || targets.routeTargetEquipmentId) return 'equipment';
  if (targets.routeTargetNodeId) return 'node';
  if (targets.routeEditInstrumentId || targets.routeTargetInstrumentId) return 'instrument';
  if (targets.urlStep !== null) return 'step';
  return null;
}

/**
 * The step that manages one instrument. An instrument is reviewed from its
 * parent's step, because that is where the parent's instruments are listed and
 * drawn active; only a context-free instrument belongs to Instruments itself.
 */
export function stepForInstrumentContext(context: InstrumentEffectiveContext): WizardStep {
  if (context.context_type === 'equipment') return 3;
  if (context.context_type === 'node') return 4;
  return 2;
}

/**
 * Picks the wizard step for the first paint after drawing state arrives.
 * Order is intentional: deep-link targets beat an explicit ?step=, which beats
 * inference from what is already on the drawing.
 *
 * An instrument target resolves through `stepForInstrumentContext`, the same
 * rule `focusInstrument` applies when it acts on that target, so the first
 * paint and the effect land on one step. Within the instrument family
 * `edit_instrument_id` names the instrument, because the effect that opens the
 * editor is the one that acts. A target naming an instrument this drawing does
 * not hold falls through to inference, because the effect warns and leaves the
 * wizard alone.
 *
 * Inference lands on the step the SME works from first. Equipment is what the
 * rest of the drawing hangs off, so a drawing with nothing on it yet opens at
 * Mark Equipments.
 */
export function resolveInitialStep(input: ResolveInitialStepInput): WizardStep {
  const { state, routeEditInstrumentId, routeTargetInstrumentId, urlStep } = input;
  switch (winningRouteTarget(input)) {
    case 'equipment':
      return 3;
    case 'node':
      return 4;
    case 'instrument': {
      const instrumentId = routeEditInstrumentId ?? routeTargetInstrumentId;
      const instrument = state.instruments.find((candidate) => candidate.id === instrumentId);
      if (instrument) return stepForInstrumentContext(getInstrumentEffectiveContext(instrument, state));
      break;
    }
    case 'step':
      return urlStep as WizardStep;
    default:
      break;
  }
  if (urlStep !== null) return urlStep;
  if (!state.pnid_no) return 1;
  if (state.equipments.length === 0 && state.nodes.length > 0) return 4;
  return 3;
}
